import {
  Box,
  Button,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemText,
  styled,
  Theme,
  Tooltip,
  Typography,
} from '@quillbot/design-system';
import Link from 'next/link';
import { Check, Close, InfoOutlined } from '@mui/icons-material';
import { makeStyles } from '@mui/styles';
import CircularGoldButton from './CircularGoldButton';
import { CHAR_LIMITS } from 'config/constants';
import Track from '../handlers/track';
import PremiumIconSvg from 'src/_common/quillElements/icon/PremiumIcon';
import ABTest from '@Common/hoc/ABTest';
import React from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '@Common/models/redux/store';

const CheckIcon = styled(Check)<{ premium?: boolean }>`
  && {
    color: ${({ theme, premium }: { theme: Theme; premium?: boolean }) =>
      premium ? theme.palette.secondary.main : theme.palette.primary.main};
  }
`;

const useStyles = makeStyles((theme: Theme) => ({
  premiumBoxMain: {
    position: 'relative',
    display: 'flex',
    justifyContent: 'center',
    margin: 'auto',
    width: '100%',
    [theme.breakpoints.down(767)]: {
      flexDirection: 'column-reverse',
      alignItems: 'center',
    },
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column-reverse',
      alignItems: 'center',
    },
  },

  premiumBox: {
    width: '100%',
    maxWidth: '438px',
    margin: '15px 17px 80px',
    position: 'relative' as 'relative',
    overflow: 'hidden',
    borderRadius: '10px',
    border: `1px solid ${theme.palette.divider}`,

    [theme.breakpoints.down('lg')]: {},

    [theme.breakpoints.down('sm')]: {
      margin: '0px 17px 30px',
      minHeight: 'auto',
    },
  },

  featureCardHeader: {
    borderTopLeftRadius: '10px',
    borderTopRightRadius: '10px',
    paddingTop: '14px',
    paddingBottom: '13px',
    display: 'flex',
    alignItems: 'center',
  },

  freeFeatureCardHeader: {
    background: theme.palette.primary.main,
  },

  premiumFeatureCardHeader: {
    background: theme.palette.secondary.main,
  },
  px24: {
    paddingRight: '24px',
    paddingLeft: '24px',
  },
  px20md: {
    [theme.breakpoints.down('md')]: {
      paddingLeft: '20px',
      paddingRight: '20px',
    },
  },
  premiumBoxInner: {
    // paddingLeft: '36px',
    // paddingRight: '36px',
    width: '100%',
    backgroundColor: theme.palette.grey[50],
    [theme.breakpoints.down('md')]: {
      // paddingLeft: '20px',
      // paddingRight: '20px',
    },
    [theme.breakpoints.down('sm')]: {
      // paddingLeft: '20px',
      // paddingRight: '20px',
    },
  },

  premiumBoxTitle: {
    fontSize: '22px',
    // margin: '0px 0px 5px',
    fontWeight: 'bold',
    textTransform: 'uppercase',
    lineHeight: '25px',
    letterSpacing: 1.5,
    [theme.breakpoints.down('sm')]: {
      fontSize: '20px',
    },
  },

  titleIconBox: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    color: '#828282',
    fontSize: '16px',
    fontWeight: 600,
    [theme.breakpoints.down('sm')]: {
      alignItems: 'flex-start',
    },
    '& svg': {
      [theme.breakpoints.down('sm')]: {
        position: 'relative' as 'relative',
        top: '3px',
      },
    },
  },

  premumBoxBtn: {
    color: theme.palette.primary.contrastText,
    borderRadius: '20px',
    fontSize: '17px',
    // marginBottom: '15px',
    marginTop: '20px',
    // maxWidth: '364px',
    fontWeight: 600,
    width: '100%',
    minHeight: '44px',

    '&:disabled': {
      backgroundColor: theme.palette.grey[400],
      color: theme.palette.text.disabled,
    },

    [theme.breakpoints.down('md')]: {
      maxWidth: '100%',
      marginLeft: 'auto',
      marginRight: 'auto',
      display: 'block',
    },

    [theme.breakpoints.down(767)]: {
      fontSize: '15px',
    },
    [theme.breakpoints.down(360)]: {
      fontSize: '15px',
      paddingLeft: '0px',
      paddingRight: '0px',
      marginTop: '20px',
      marginBottom: '10px',
      marginLeft: 'auto',
      marginRight: 'auto',
    },
  },

  h48: {
    height: '48px',
  },
  hAutoSm: {
    [theme.breakpoints.down(767)]: {
      height: 'auto',
    },
  },
  iconList: {
    padding: '2px 0px',
    marginBottom: '45px',
    '& li': {
      padding: '4px 0px',
      margin: '4px 0px 12px',
      alignItems: 'flex-start',
      [theme.breakpoints.down('sm')]: {
        margin: '0px 0px 0px',
      },
      [theme.breakpoints.down(376)]: {
        alignItems: 'flex-start',
      },
    },

    [theme.breakpoints.down('sm')]: {
      marginBottom: '35px',
    },
  },
  listItem: {
    height: '54px',
    borderBottomWidth: '1px',
    borderBottomStyle: 'solid',
    borderColor: theme.palette.divider,
    paddingRight: '24x',
    paddingLeft: '24px',
    [theme.breakpoints.down('md')]: {
      paddingRight: '20px',
      paddingLeft: '20px',
    },
    [theme.breakpoints.down('sm')]: {
      paddingRight: '16px',
      paddingLeft: '16px',
    },
  },
  listItemText: {
    '& span': {
      lineHeight: '21px',
      [theme.breakpoints.down('md')]: {
        fontSize: '14px',
      },
    },
  },
  p0: { padding: 0 },

  listCheckIcon: {
    marginRight: '10px',
    [theme.breakpoints.down(376)]: {
      marginTop: '5px',
    },
  },

  premiumTooltip: {
    backgroundColor: '#323232',
    padding: '8px 14px 18px',
    maxWidth: 296,
    // right: -5,
  },
  tooltipTitle: {
    color: theme.specialColors.WARNING_BAR,
    fontSize: 16,
    fontWeight: 600,
    marginBottom: 8,
    lineHeight: '1.5',
  },
  tooltipSubtext: {
    fontSize: 13,
    lineHeight: '17.7px',
    color: theme.specialColors.WHITE_TEXT,
  },
  tooltipImg: {
    maxWidth: '100%',
    marginTop: 12,
  },
  tooltipIcon: {
    cursor: 'pointer',
    color: theme.specialColors.GRAY_BORDER_COLOR,
    '&:hover': {
      color: theme.specialColors.WARNING_BAR,
    },
  },
  tooltipArrow: {
    color: '#323232',
  },

  boxItemFooter: {
    backgroundColor: theme.palette.grey[500],
    textAlign: 'center' as 'center',
    // position: 'absolute' as 'absolute',
    bottom: '0px',
    left: '0px',
    width: '100%',
    padding: '17px 0px',
    fontSize: '15px',
    fontWeight: 600,
  },

  plagCheckTerms: {
    position: 'absolute',
    bottom: '45px',
    right: '0',
    width: '100%',
    textAlign: 'center',
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
    [theme.breakpoints.down('xs')]: {
      display: 'none',
    },
  },
  noMargin: {
    margin: '0px',
  },
}));

const freeList = [
  {
    title: '125 words in the Paraphraser',
    included: true,
  },
  {
    title: 'Standard and Fluency modes',
    included: true,
  },
  {
    title: '3 synonyms options',
    included: true,
  },
  {
    title: '1 Freeze word or phrase',
    included: true,
  },
  {
    title: '1200 words in the Summarizer',
    included: true,
  },
  {
    title: 'Faster processing speed',
    included: false,
  },
  {
    title: 'Advanced grammar rewrites',
    included: false,
  },
  {
    title: 'Compare Modes (Desktop only)',
    included: false,
  },
  {
    title: 'Plagiarism Checker*',
    included: false,
  },
];

const premiumList = [
  {
    title: 'Unlimited words in the Paraphraser',
    tooltip: {
      heading: 'Write Faster',
      subText: 'You’ll complete your work up to 75% faster with QuillBot.',
    },
  },
  {
    title: 'Standard, Fluency, Expand, Shorten, Formal, Simple, and Creative modes',
    tooltip: {
      heading: 'Explore Multiple Styles',
      subText: "There's a mode for every occasion, whether formal or casual.",
      imgUrl: '/images/theme/commonImages/premiumPage/multiple-styles-tooltip.png',
    },
  },
  {
    title: '4 synonyms options',
    tooltip: {
      heading: 'Add More Synonyms',
      subText: 'Full use of the Synonym Slider can increase the uniqueness of your text.',
      imgUrl: '/images/theme/commonImages/premiumPage/synonyms-tooltip.png',
    },
  },
  {
    title: 'Unlimited Freeze words and phrases',
    tooltip: {
      heading: 'Prevent Words From Changing',
      subText:
        "Industry-specific terms (technical, scientific, medical, etc.) won't get changed during paraphrasing.",
      imgUrl: '/images/theme/commonImages/premiumPage/freeze-tooltip.png',
    },
  },
  {
    title: '6000 words in the Summarizer',
    tooltip: {
      heading: 'Find The Key Points',
      subText: 'Research time is greatly reduced when articles are trimmed to their main points.',
      imgUrl: '/images/theme/commonImages/premiumPage/key-points-tooltip.png',
    },
  },
  {
    title: 'Faster processing speed',
    tooltip: {
      heading: 'Enjoy Increased Paraphrasing Speed',
      subText:
        'Fifteen sentences are paraphrased at once instead of only two sentences with a free account.',
      imgUrl: '/images/theme/commonImages/premiumPage/paraphrasing-speed-tooltip.png',
    },
  },
  {
    title: 'Advanced grammar rewrites',
    tooltip: {
      heading: 'Get Immediate Recommendations',
      subText:
        "Rewritten sentences are suggested for text that is too complicated or doesn't flow well.",
      imgUrl: '/images/theme/commonImages/premiumPage/recommendations-tooltip.png',
    },
  },
  {
    title: 'Compare Modes (Desktop only)',
    tooltip: {
      heading: 'Choose The Best Rewrite',
      subText: "You'll be able to compare outputs from multiple modes.",
      imgUrl: '/images/theme/commonImages/premiumPage/compare-modes-tooltip.png',
    },
  },
  {
    title: 'Plagiarism Checker*',
    tooltip: {
      heading: 'Detect Plagiarism',
      subText:
        'You can avoid accidental plagiarism by ensuring all outside sources are properly cited.',
      imgUrl: '/images/theme/commonImages/premiumPage/plagiarism-tooltip.png',
    },
  },
];

interface TooltipItem {
  heading: string;
  subText: string;
  imgUrl?: string;
}
interface ListItemProps {
  title: string;
  included?: boolean;
  tooltip?: TooltipItem;
  premium?: boolean;
}

const ComparisonListItem = ({ title, included = true, tooltip, premium = true }: ListItemProps) => {
  const classes = useStyles();

  return (
    <ListItem className={classes.listItem}>
      {included &&
        (premium ? (
          <CheckIcon premium className={classes.listCheckIcon} />
        ) : (
          <CheckIcon className={classes.listCheckIcon} />
        ))}
      {!included && <Close color="error" className={classes.listCheckIcon} />}
      <ListItemText className={`${classes.noMargin} ${classes.listItemText}`} primary={title} />
      {tooltip && (
        <Tooltip
          classes={{
            tooltip: classes.premiumTooltip,
            arrow: classes.tooltipArrow,
          }}
          enterTouchDelay={50}
          leaveTouchDelay={2500}
          arrow
          placement="bottom-end"
          title={
            <>
              <div className={classes.tooltipTitle}>{tooltip.heading}</div>
              <div className={classes.tooltipSubtext}>{tooltip.subText}</div>
              {tooltip.imgUrl && (
                <img className={classes.tooltipImg} src={tooltip.imgUrl} alt={tooltip.heading} />
              )}
            </>
          }
        >
          <InfoOutlined className={classes.tooltipIcon} />
        </Tooltip>
      )}
    </ListItem>
  );
};

interface IProps {
  signUpPathname: string;
  isUserAuthenticated: boolean;
  isUserPremium: boolean;
}

const PremiumVsFreeFeatureTable = (props: IProps) => {
  const { signUpPathname, isUserAuthenticated, isUserPremium } = props;
  const availedRefund = useSelector((rootState: RootState) => rootState?.user?.availedRefund);

  const classes = useStyles();

  const freeTable = (
    <Box boxShadow={3} className={classes.premiumBox}>
      <Box className={`${classes.featureCardHeader} ${classes.freeFeatureCardHeader}`}>
        <div>
          <Typography
            variant="h3"
            color="primary.contrastText"
            className={`${classes.premiumBoxTitle} ${classes.px24} ${classes.px20md}`}
          >
            Free
          </Typography>
          <Box className={classes.titleIconBox}>
            {/* <CreateOutlinedIcon style={{ marginRight: '6px' }} /> For casual writing */}
          </Box>
        </div>
      </Box>
      <Box className={`${classes.premiumBoxInner} `}>
        {signUpPathname && (
          <>
            <Box className={`${classes.px24} ${classes.px20md}`}>
              <Link
                href={{
                  pathname: '/signup',
                  query: {
                    returnUrl: signUpPathname,
                    referrer: 'premiumPage',
                  },
                }}
              >
                <Button
                  variant="contained"
                  color="primary"
                  disabled={isUserAuthenticated}
                  className={classes.premumBoxBtn}
                >
                  {!isUserAuthenticated && 'Sign Up - It’s Free'}
                  {isUserAuthenticated && !isUserPremium && 'Your Current Plan'}
                  {isUserAuthenticated && isUserPremium && 'Features'}
                </Button>
              </Link>
            </Box>
            <Box pt="20px" />
            <Divider />
          </>
        )}

        <List component="ul" className={classes.p0} aria-label="contacts">
          {freeList.map((freeListItem, index) => (
            <ComparisonListItem
              premium={false}
              title={freeListItem.title}
              included={freeListItem.included}
              key={`${freeListItem.title}-${index}`}
            />
          ))}
        </List>
        <Box className={classes.boxItemFooter}>No Credit Card Required</Box>
      </Box>
    </Box>
  );

  const premiumTable = (
    <Box boxShadow={3} className={classes.premiumBox}>
      <Box
        className={`${classes.premiumFeatureCardHeader} ${classes.px24} ${classes.px20md} ${classes.featureCardHeader}`}
      >
        <PremiumIconSvg
          style={{
            width: 23,
            height: 22,
            marginRight: 8,
          }}
          transparent
        />
        <Typography
          variant="h3"
          color="secondary.contrastText"
          className={classes.premiumBoxTitle}
          display="inline"
        >
          PREMIUM
        </Typography>
        <Box className={classes.titleIconBox}>
          {/* <QueryBuilderOutlinedIcon style={{ marginRight: '6px' }} />
                  For papers with high time sensitivity */}
        </Box>
      </Box>
      <Box style={{ backgroundColor: 'grey.50' }} className={classes.premiumBoxInner}>
        {signUpPathname && (
          <>
            <Box className={`${classes.px24} ${classes.px20md}`}>
              <Link
                href={{
                  pathname: '/upgrade',
                  query: { referrer: 'premiumPage' },
                }}
              >
                <CircularGoldButton
                  variant="contained"
                  // color="secondary"
                  featured={1}
                  onClick={() => {
                    Track.upgradeLinkClicked('premiumPropsTable');
                  }}
                  disabled={isUserPremium}
                  className={classes.premumBoxBtn}
                >
                  {isUserPremium ? 'Your Current Plan' : 'Upgrade to Premium'}
                </CircularGoldButton>
              </Link>
            </Box>
            <Box pt="20px" />
            <Divider />
          </>
        )}
        <List component="nav" className={classes.p0} aria-label="contacts">
          <ABTest experiment="premiumTooltips_May2022" default>
            <React.Fragment>
              {premiumList.map((premiumItem, index) => (
                <ComparisonListItem
                  title={premiumItem.title}
                  tooltip={premiumItem.tooltip}
                  key={`${premiumItem.title}-${index}`}
                />
              ))}
            </React.Fragment>
          </ABTest>
          <ABTest variant="A" experiment="premiumTooltips_May2022">
            <React.Fragment>
              {premiumList.map((premiumItem, index) => (
                <ComparisonListItem
                  title={premiumItem.title}
                  key={`${premiumItem.title}-${index}`}
                />
              ))}
            </React.Fragment>
          </ABTest>
          <ABTest variant="B" experiment="premiumTooltips_May2022">
            <React.Fragment>
              {premiumList.map((premiumItem, index) => (
                <ComparisonListItem
                  title={premiumItem.title}
                  tooltip={premiumItem.tooltip}
                  key={`${premiumItem.title}-${index}`}
                />
              ))}
            </React.Fragment>
          </ABTest>
        </List>

        {!availedRefund && <Box className={classes.boxItemFooter}>3 Day Money-Back Guarantee</Box>}
      </Box>
    </Box>
  );

  return (
    <Grid item xs={12} sm={12} md={12} lg={12}>
      <Box className={classes.premiumBoxMain}>
        {freeTable}
        {premiumTable}

        <div className={classes.plagCheckTerms}>*Scan up to 20 pages a month</div>
      </Box>
      <Box pt="18px" />
    </Grid>
  );
};

export default PremiumVsFreeFeatureTable;
